import { useQuery } from "@tanstack/react-query";
import { ClipboardCopy, Download, FileCode2, Package } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, api } from "@/lib/api";

const copyLua = async () => {
  try {
    const { data } = await api.get("/tts/importer/terminal.lua", { responseType: "text" });
    await navigator.clipboard.writeText(data);
    toast.success("Lua copied. In TTS: right-click object → Scripting → Scripting Editor → paste → Save & Play");
  } catch {
    toast.error("Copy failed. Use the Lua source download instead");
  }
};

export function TtsTerminalPanel() {
  const { data: info, isError } = useQuery({ queryKey: ["tts", "version"], queryFn: () => api.get("/tts/importer/version").then((r) => r.data) });
  return (
    <div className="panel p-5 text-sm text-slate-300" data-testid="developers-tts-section">
      <p className="eyebrow mb-3">Tabletop Simulator · Deck Import Terminal</p>
      {info && (
        <p className="mb-3 font-mono text-xs text-aether-300" data-testid="tts-version-info">
          Latest v{info.latest_stable} · minimum v{info.minimum_supported} · released {info.release_date}
        </p>
      )}
      {isError && <p className="mb-3 text-xs text-red-300" data-testid="tts-version-error">Version information is unavailable right now.</p>}
      <p className="mb-3">A Lua-scripted object for Tabletop Simulator. Place it on any MTG table, click <span className="text-amber-200">Open Deck Importer</span>, paste a published public or unlisted deck link and the deck spawns at your seat, with each zone kept separate.</p>
      <div className="mb-4 flex flex-wrap gap-2">
        <a href={`${BACKEND_URL}/api/v1/tts/importer/package.zip`} className="btn-gold" data-testid="tts-download-package"><Package className="h-4 w-4" />Workshop package (.zip)</a>
        <button type="button" onClick={copyLua} className="btn-outline" data-testid="tts-copy-lua"><ClipboardCopy className="h-4 w-4" />Copy Lua</button>
        <a href={`${BACKEND_URL}/api/v1/tts/importer/saved-object.json`} className="btn-outline" data-testid="tts-download-saved-object"><Download className="h-4 w-4" />Saved Object (.json)</a>
        <a href={`${BACKEND_URL}/api/v1/tts/importer/terminal.lua`} className="btn-outline" data-testid="tts-download-lua"><FileCode2 className="h-4 w-4" />Lua source</a>
        <a href={`${BACKEND_URL}/api/v1/tts/importer/terminal.xml`} className="btn-outline" data-testid="tts-download-xml"><FileCode2 className="h-4 w-4" />XML UI</a>
      </div>
      <ol className="mb-3 list-decimal space-y-1 pl-5 text-xs text-slate-400">
        <li>Copy the .json into <code className="font-mono text-amber-200">Documents/My Games/Tabletop Simulator/Saves/Saved Objects/</code>.</li>
        <li>In TTS: Objects → Saved Objects → The Planar Foundry — Deck Import Terminal.</li>
        <li>Endpoints: <code className="font-mono">GET /decks/{"{deck_id}"}/tts</code> (manifest v3) and <code className="font-mono">GET /tts/importer/version</code>.</li>
      </ol>
      <p className="text-copper-200" data-testid="tts-status-note">
        {info?.workshop_published ? <a href={info.workshop_url} target="_blank" rel="noreferrer" className="underline" data-testid="tts-workshop-link">Steam Workshop page</a> : "Not yet published on the Steam Workshop."}
        {" "}The importer is built but has not yet been verified inside Tabletop Simulator.
      </p>
    </div>
  );
}
